// The clients list. A row now DRILLS DOWN to the client card at /clients/:id
// rather than opening an edit form — a client has more to say than a form's worth
// (their jobs, the coverage they received), and editing lives inside that card.
// Creating a new client still happens here, in a modal: there is no card to open yet.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAsync from '../../hooks/useAsync';
import useTableSort, { cellText } from '../../hooks/useTableSort';
import { useLookups } from '../../context/LookupsContext';
import { clientsApi } from '../../api';
import he from '../../i18n/he';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary from '../../components/Loader';
import DataTable from '../../components/DataTable';
import Button from '../../components/Button';
import ClientModal from '../../components/ClientModal';
import SearchBox from '../../components/SearchBox';

export default function Clients() {
  const lookups = useLookups();
  const navigate = useNavigate();
  // Customers only — everyone who is no longer a prospect. Leads are client rows, but
  // they have their own screen, and after a spreadsheet import they arrive by the
  // hundred; listing them here buried the customers among prospects nobody had yet
  // agreed to work with. The filter is the SERVER's (`scope=nonleads`), not a slice
  // taken after the fetch, so the prospects never travel to a screen that shows none
  // of them. A won lead keeps its row and appears here the moment it converts.
  const { data, loading, error, reload } = useAsync(() => clientsApi.list('nonleads'));
  const [creating, setCreating] = useState(false);
  // Which half of the list is on screen. A VIEW filter and nothing else — both halves
  // arrive in the one fetch above and are split here, so switching is instant and an
  // inactive client is never actually gone, just on the other tab.
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');

  const columns = [
    { key: 'name', label: he.clients.name, primary: true },
    { key: 'stage', label: he.clients.stage, render: (r) => he.leadStages[r.stage] || r.stage },
    { key: 'owner_name', label: he.clients.owner, render: (r) => r.owner_name || '—' },
    { key: 'contact_name', label: he.clients.contact, render: (r) => r.contact_name || '—' },
    { key: 'phone', label: he.clients.phone, render: (r) => r.phone || '—' },
    { key: 'payment_terms', label: he.clients.terms, render: (r) => lookups.termLabel(r.payment_terms) },
  ];

  // What the search looks at: every column the table shows — so a row that matches is
  // one the partner can SEE matching — plus the email and every contact person (071),
  // who may not be the main one shown in the row. The email is how a client is often
  // identified even though the list has no room for a column of them. Each column is
  // read through `cellText`, the same extractor the sort uses, so a search matches the
  // Hebrew label on screen (סטטוס, תנאי תשלום) and not the machine code beneath it.
  const term = search.trim().toLowerCase();
  const matches = (r) => !term
    || [...columns.map((c) => cellText(c, r)), r.email, r.contacts_text]
      .some((v) => v != null && String(v).toLowerCase().includes(term));

  // Three passes, in the order a partner would describe them: which half of the list,
  // then what they typed, then how they want it ordered. Search and sort are both
  // client-side because the whole list is already here — the one fetch above brings
  // every non-lead row and the active/inactive tabs already split it in the browser.
  const visible = (data || []).filter((c) => (showInactive ? !c.is_active : !!c.is_active));
  // Seeded with the order the server already sent (`ORDER BY clients.name`), so the
  // arrow on the name column states how the table is actually sorted from the first
  // render rather than appearing only once something is clicked.
  const { sort, toggle, rows } = useTableSort(columns, visible.filter(matches), { key: 'name', dir: 'asc' });

  const emptyText = () => {
    if (term) return he.empty.clientSearch;
    return showInactive ? he.empty.inactiveClients : he.empty.clients;
  };

  return (
    <>
      <PageHeader title={he.clients.title} spread>
        <div className="row">
          <div className="seg">
            <Button small variant={showInactive ? 'subtle' : ''} onClick={() => setShowInactive(false)}>
              {he.clients.showActive}
            </Button>
            <Button small variant={showInactive ? '' : 'subtle'} onClick={() => setShowInactive(true)}>
              {he.clients.showInactive}
            </Button>
          </div>
          <SearchBox value={search} onChange={setSearch} placeholder={he.clients.search} />
        </div>
        <Button onClick={() => setCreating(true)}>{he.clients.newClient}</Button>
      </PageHeader>
      <div className="card">
        <AsyncBoundary loading={loading} error={error}>
          <DataTable
            columns={columns}
            rows={rows}
            sort={sort}
            onSort={toggle}
            onRowClick={(r) => navigate(`/clients/${r.id}`)}
            empty={{ text: emptyText() }}
          />
        </AsyncBoundary>
      </div>
      {creating && (
        <ClientModal
          client={{}}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); reload(); }}
        />
      )}
    </>
  );
}
